import type { CaseWorkflowTrigger, ProposalStatus } from '@/constants';
import type {
	ICase,
	ICaseLogResponse,
	ICaseSumarizedItem,
	IGridifyPaging,
	IOccurrenceReviewHistory,
	IOccurrenceReviewRequest,
	IOccurrenceReviewScope,
	IResponseBase,
} from '@/interfaces';
import type { ICaseRequest } from '@/interfaces/ICaseRequest';
import type {
	IDealReactivationPreview,
	IDealReactivationResult,
} from '@/interfaces/IDealReactivation';
import type { ICaseResponse } from '@/interfaces/IProposalResponse';
import type { IUpdateCaseWithProductRequest } from '@/interfaces/IUpdateCaseWithProductRequest';
import type { IGridifyQuery } from '@/models';
import type { CaseResponseType } from '@/models/CaseResponseType';
import type { CollectionResume, ICaseTrackingStatusResponse } from '@/models/dto/Case.dto';
import type { FinancialStatementResponse } from '@/models/dto/FinancialStatement.dto';
import type { IProductOffer } from '@/models/dto/Product.dto';
import type {
	CollectionDetailResponse,
	CollectionInsightsResponse,
	CollectionListItem,
} from '@/features/Cases/Collections/Types/CollectionTypes';
import { Http, HttpService } from './http';

const http = Http.create();

export interface IAdminOccurrenceEditResponse {
	quotation: unknown;
	exceededLimits: boolean;
	limitWarning: string | null;
}

export interface IDuplicateCaseView {
	caseId: string;
	renterName: string;
	caseTypeLabel: string;
	statusLabel: string;
	isContract: boolean;
}

/** Veredito do gate de duplicidade para uma proposta existente (clique em "Assinar"). */
export interface ICaseTenantDuplicatesResponse {
	sameBroker: IDuplicateCaseView[];
	/**
	 * Quantos casos de outros parceiros existem, sem os dados deles. A API manda só a contagem:
	 * caseId, nome do inquilino e status de proposta alheia não são do parceiro que está na tela.
	 */
	otherBrokerCount: number;
	/** Duplicidade no mesmo parceiro: o corretor escolhe entre Continuar e Cancelar as anteriores. */
	requiresDecision: boolean;
	/** Só em outro parceiro: segue por documentos de renda, sem escolha. */
	requiresIncomeDocuments: boolean;
	/** Já passou por este fluxo antes: nada é exigido de novo. */
	alreadyGated: boolean;
}

export class CaseService extends HttpService {
	static async updateCase(value: ICaseResponse) {
		const response = await http.patch(`/cases/${value.id}`, value);
		return response.data;
	}

	/**
	 * Recalculate the case quotation. When includeSetup is true the existing setup is kept,
	 * when false the setup is zeroed.
	 */
	static async reCalculate(id: string, includeSetup: boolean) {
		const response = await http.patch(`/v2/cases/${id}/re-calculate`, null, {
			params: { includeSetup },
		});
		return response.data;
	}
	/**
	 * Create a new proposal
	 * @param proposalRequest
	 * @returns
	 */
	static async addCase(proposalRequest: ICaseRequest) {
		const response = await http.post<IResponseBase<ICase>>('/cases', proposalRequest);
		return response.data;
	}

	/**
	 * Veredito do gate de duplicidade para ESTA proposta, antes de enviar para assinatura. O backend
	 * exclui o próprio caso da busca e já considera se ele passou pelo fluxo de renda antes — quando
	 * isso ocorre, `alreadyGated` volta true e nada é exigido de novo.
	 */
	static async getTenantDuplicatesForCase(caseId: string) {
		const response = await http.get<IResponseBase<ICaseTenantDuplicatesResponse>>(
			`/cases/${caseId}/tenant-duplicates`,
		);
		return response.data.data;
	}

	/**
	 * Return a pagination of proposals
	 * @param query
	 * @returns
	 *
	 */
	static async getCases(query: IGridifyQuery | null = null) {
		const response = await http.get<IGridifyPaging<ICase>>('/cases', {
			params: query,
		});
		return response.data;
	}

	static async getCasesV2(query: IGridifyQuery | null = null, view: string = '') {
		const response = await http.get<IGridifyPaging<CaseResponseType>>(`v2/cases/${view}`, {
			params: query,
		});

		return response.data;
	}

	/** Listagem dedicada de casos de Seguro Incêndio (prêmio, forma de pagamento e data de aniversário). */
	static async getFireInsuranceCases(query: IGridifyQuery | null = null) {
		const response = await http.get<IGridifyPaging<CaseResponseType>>('v2/cases/fire-insurance', {
			params: query,
		});

		return response.data;
	}

	static async getCasesSumarized(query: IGridifyQuery | null = null) {
		const cancelToken = this.createCancelToken('getCasesSumarized');

		const response = await http.get<IResponseBase<ICaseSumarizedItem[]>>('/cases/sumarized', {
			params: query,
			cancelToken: cancelToken,
		});

		this.cancelTokens['getCasesSumarized'] = null;

		return response.data;
	}

	/**
	 * Return a proposal by id
	 * @param id
	 * @param complete Include nested values (Quotation, Product, Renter)
	 * @returns
	 */
	static async getCaseById(id: string, complete: boolean = false) {
		const response = await http.get<IResponseBase<ICaseResponse>>(
			`cases/${id}?complete=${complete}`,
		);
		return response.data;
	}

	static async cancelCase(caseId: string, reason: string) {
		const response = await http.post<IResponseBase<ICaseResponse>>(`cases/${caseId}/cancel`, {
			caseId,
			reason,
		});
		return response.data;
	}

	static async getCaseLogs(id: string, query: IGridifyQuery) {
		const response = await http.get<IGridifyPaging<ICaseLogResponse>>(`cases/${id}/logs`, {
			params: query,
		});
		return response.data;
	}

	static async updateStatus(id: string, status: ProposalStatus) {
		const response = await http.patch<IResponseBase<ICaseResponse>>(`cases/${id}/status/${status}`);
		return response.data;
	}

	/**
	 * Escopo do checklist da ocorrência: quais itens exibir (completo × reduzido) conforme o
	 * histórico do contrato e se a ocorrência é "final". Fonte da verdade no backend.
	 */
	static async getOccurrenceReviewScope(caseId: string) {
		const response = await http.get<IResponseBase<IOccurrenceReviewScope>>(
			`cases/${caseId}/occurrence-review/scope`,
		);
		return response.data;
	}

	/**
	 * Movimentação de ocorrência com checklist do analista (ADMIN/USER): move o status e
	 * registra o review (tabela dedicada) + observação interna no backend.
	 */
	static async submitOccurrenceReview(caseId: string, payload: IOccurrenceReviewRequest) {
		const response = await http.post<IResponseBase<ICaseResponse>>(
			`cases/${caseId}/occurrence-review`,
			payload,
		);
		return response.data;
	}

	/**
	 * Histórico de checklists das ocorrências de um contrato (ADMIN/USER). `caseId` pode ser o id do
	 * contrato ou de qualquer ocorrência — o backend normaliza para o contrato e retorna o conjunto
	 * completo, então a aba mostra o mesmo em ambas as telas.
	 */
	static async getOccurrenceReviews(caseId: string) {
		const response = await http.get<IResponseBase<IOccurrenceReviewHistory>>(
			`cases/${caseId}/occurrence-reviews`,
		);
		return response.data;
	}

	static getDocumentFiller(caseId: string, bankId: string) {
		return `${import.meta.env.VITE_API_SERVICE}/documentfiller/cases/${caseId}/fill/${bankId}`;
	}

	static async getCaseCollectionResume(caseId: string) {
		const response = await http.get<IResponseBase<CollectionResume>>(
			`cases/${caseId}/collection-resume`,
		);
		return response.data;
	}

	static async getFinancialStatement(caseId: string) {
		const response = await http.get<IResponseBase<FinancialStatementResponse>>(
			`cases/${caseId}/financial-statement`,
		);
		return response.data;
	}

	/**
	 * Ficha financeira em PDF (ENG-622), gerada na hora pela API. Retorna o blob bruto, como o
	 * exportCollections; o nome do arquivo é montado na tela (financialStatementFileName).
	 */
	static async downloadFinancialStatementPdf(caseId: string) {
		const response = await http.get<Blob>(`cases/${caseId}/financial-statement/pdf`, {
			responseType: 'blob',
		});
		return response.data;
	}

	static async updateOccurrenceAndCollection(caseId: string, quotation: any) {
		const response = await http.put(`/cases/${caseId}/occurrence-collection`, quotation);
		return response.data;
	}

	/**
	 * Edição administrativa (ADMIN) de uma ocorrência já aprovada. Exige um motivo, que é
	 * registrado apenas na observação interna. Retorna se os valores excederam os limites do
	 * contrato (bypass com aviso).
	 */
	static async updateApprovedOccurrenceByAdmin(caseId: string, quotation: any, reason: string) {
		const response = await http.put<IResponseBase<IAdminOccurrenceEditResponse>>(
			`/cases/${caseId}/occurrence-collection/admin`,
			{ quotation, reason },
		);
		return response.data;
	}

	static async refundCase(caseId: string, refundedAt: string) {
		const response = await http.patch<IResponseBase<ICaseResponse>>(`v2/cases/${caseId}/refund`, {
			refundedAt,
		});
		return response.data;
	}

	static async getRelatedCases(caseId: string) {
		const response = await http.get<IResponseBase<ICaseResponse[]>>(`/cases/${caseId}/related`);
		return response.data;
	}

	/**
	 * Cria uma ocorrência e envia os arquivos em uma única transação.
	 * @param caseData Objeto com os dados da ocorrência (CaseRequest)
	 * @param files Lista de arquivos do NaiveUI (UploadFileInfo[])
	 * @param docTypesMap Objeto mapeando ID do arquivo -> DocumentType
	 */
	static async createCaseWithFiles(caseData: any, files: any[], docTypesMap: Record<string, any>) {
		const bodyFormData = new FormData();

		bodyFormData.append('caseRequestJson', JSON.stringify(caseData));

		files.forEach((fileWrapper) => {
			if (fileWrapper.file) {
				bodyFormData.append('files', fileWrapper.file);

				const type = docTypesMap[fileWrapper.id];

				if (type !== undefined && type !== null) {
					bodyFormData.append('docTypes', type.toString());
				}
			}
		});

		const response = await http.post('/cases/create-with-files', bodyFormData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		});

		return response.data;
	}

	/** Prévia da reativação de acordo distratado (ENG-614). Só ADMIN. */
	static async getDealReactivationPreview(caseId: string) {
		const response = await http.get<IResponseBase<IDealReactivationPreview>>(
			`cases/${caseId}/deal-reactivation`,
		);
		return response.data.data;
	}

	/** Reativa o acordo distratado: volta a vigente e regera as cobranças canceladas. Só ADMIN. */
	static async reactivateDeal(caseId: string) {
		const response = await http.post<IResponseBase<IDealReactivationResult>>(
			`cases/${caseId}/deal-reactivation`,
		);
		return response.data.data;
	}

	static async forceStatusToPaid(caseId: string) {
		const response = await http.patch<IResponseBase<ICaseResponse>>(
			`cases/${caseId}/force-status-paid`,
		);
		return response.data;
	}

	static async forceStatusToApproved(caseId: string) {
		const response = await http.patch<IResponseBase<ICaseResponse>>(
			`cases/${caseId}/force-status-approved`,
		);
		return response.data;
	}

	/**
	 * Dispara um trigger de workflow do caso via ToolsController.
	 * O backend publica em fila e ativa os fluxos associados —
	 * não basta alterar o status diretamente.
	 */
	static async triggerCaseWorkflow(caseId: string, trigger: CaseWorkflowTrigger) {
		const response = await http.post<IResponseBase<unknown>>(`tools/case-workflow-trigger`, {
			caseId,
			trigger,
		});
		return response.data;
	}

	/**
	 * Atualiza um caso com um produto selecionado
	 * @param caseId ID do caso
	 * @param productId ID do produto
	 * @param request Dados adicionais para atualização (comissão, seguro incêndio, dados do locador)
	 * @returns Resposta da API
	 */
	static async updateCaseWithProduct(
		caseId: string,
		productId: string,
		request?: IUpdateCaseWithProductRequest,
	) {
		const response = await http.patch<IResponseBase<ICaseResponse[]>>(
			`v2/cases/${caseId}/product/${productId}`,
			request,
		);
		return response.data;
	}

	/**
	 * Adiciona um corresponsável (renter) ao caso
	 * @param caseId ID do caso
	 * @param renter Dados do corresponsável (ClientRequest)
	 * @returns Resposta da API
	 */
	static async addRenterToCase(caseId: string, renter: any) {
		const response = await http.post<IResponseBase<IClient>>(`v2/cases/${caseId}/renter`, renter);
		return response.data;
	}

	static async getProductOffers(caseId: string) {
		const response = await http.get<IResponseBase<IProductOffer[]>>(`/cases/${caseId}/offers`);
		return response.data;
	}

	static async getCaseStatusTrackLogs(caseId: string) {
		const response = await http.get<IResponseBase<ICaseTrackingStatusResponse[]>>(
			`/cases/${caseId}/tracking`,
		);
		return response.data;
	}

	/**
	 * Página de cobranças ativas (grid completo). Continua existindo, mas não é
	 * mais o load inicial da tela de cobranças.
	 */
	static async getCollectionsV2(query: IGridifyQuery) {
		const response = await http.get<IGridifyPaging<CollectionListItem>>('/cases/collections/v2', {
			params: query,
		});
		return response.data;
	}

	/**
	 * Detalhe completo de uma cobrança (cliente, imóvel, mensagens e ligações).
	 */
	static async getCollectionDetail(collectionId: string) {
		const response = await http.get<IResponseBase<CollectionDetailResponse>>(
			`/cases/collections/${collectionId}/detail`,
		);
		return response.data;
	}

	/**
	 * Indicadores agregados das cobranças (cards de estatística).
	 */
	static async getCollectionsInsights() {
		const response = await http.get<IResponseBase<CollectionInsightsResponse>>(
			'/cases/collections/insights',
		);
		return response.data;
	}

	/**
	 * Exporta as cobranças em planilha (xlsx). Retorna o blob bruto.
	 */
	static async exportCollections(query: IGridifyQuery) {
		const response = await http.get('/cases/collections/export', {
			params: query,
			responseType: 'blob',
		});
		return response.data;
	}

	/**
	 * Retira a cobrança atual da fila de cobrança.
	 */
	static async removeCollectionFromQueue(collectionId: string) {
		const response = await http.post(`/cases/collections/${collectionId}/remove-from-queue`);
		return response.data;
	}
}

export default CaseService;
